import { Alert, Button, Modal, Space, Spin, Typography } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { messageFor } from '@/lib/api/index.js';
import { space } from '@/design/tokens.js';
import { fetchPolicyDocument, policyDocumentUrl } from './policies.api.js';
import { PolicyDocument } from './PolicyDocument.js';

const { Text } = Typography;

export interface ReadablePolicy {
  policyId: string;
  versionId: string;
  title: string;
}

export interface PolicyReaderProps {
  /** The policy to read, or `null` for closed. */
  policy: ReadablePolicy | null;
  onClose: () => void;
  /** Omitted when there is nothing left to acknowledge on this one. */
  acknowledge?: { pending: boolean; onAcknowledge: () => void };
}

/**
 * Reading a policy, and saying so, in one window (orbit-ui#171).
 *
 * **The document opens here rather than in a new tab.** A tab takes somebody
 * away from the screen that asked them to read it: they read the PDF, close
 * it, and are back where they started with no sign that anything happened —
 * and on a blocking prompt they come back to a modal they cannot dismiss.
 *
 * **The confirmation lives with the document, not beside the link to it.**
 * It used to sit in the list, which meant the shortest route to clearing a
 * mandatory policy was to press a button next to a document nobody had
 * opened. Here, the only button that says you have read it is on the thing
 * you are reading.
 *
 * **Fetched as a blob rather than framed by URL.** The document is served by
 * the API, on another origin in production, and a cross-site frame is at the
 * mercy of the cookie's `SameSite` rule and the API's own frame headers — so
 * it would work in development and show an empty box in production. Fetching
 * it with the session cookie is the same request the browser was making
 * anyway.
 *
 * **Drawn by `PolicyDocument`, not framed** (orbit-ui#185). An `<iframe>`
 * shows a PDF on a laptop and a dead "Open" button on a phone, because
 * mobile browsers have no built-in viewer to hand it to. Rasterising the
 * pages ourselves is the one approach that behaves the same everywhere.
 */
export function PolicyReader({ policy, onClose, acknowledge }: PolicyReaderProps) {
  const [file, setFile] = useState<Blob | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const policyId = policy?.policyId ?? null;
  const versionId = policy?.versionId ?? null;

  // Stable, so the renderer does not restart every time this re-renders.
  const onFailure = useCallback((message: string) => setFailure(message), []);

  useEffect(() => {
    if (!policyId || !versionId) return;

    /*
      `cancelled` is for the close that happens mid-fetch: without it a late
      response sets state on a window that has gone.

      No object URL any more — the blob goes straight to the renderer, which
      reads it once into an ArrayBuffer. The old code made a `blob:` URL for
      the iframe and had to remember to revoke it.
    */
    let cancelled = false;

    setFile(null);
    setFailure(null);

    fetchPolicyDocument(policyId, versionId)
      .then((blob) => {
        if (!cancelled) setFile(blob);
      })
      .catch((error: unknown) => {
        if (!cancelled) setFailure(messageFor(error));
      });

    return () => {
      cancelled = true;
    };
  }, [policyId, versionId]);

  return (
    <Modal
      open={policy !== null}
      onCancel={onClose}
      title={policy?.title ?? ''}
      width={880}
      // Tall, because this is a document. The top margin is small rather than
      // centred: a centred tall modal loses its footer off the bottom of a
      // laptop screen, which is where the button somebody needs is.
      style={{ top: space[5], maxWidth: 'calc(100vw - 32px)' }}
      styles={{ body: { height: '68vh', padding: 0, overflow: 'hidden' } }}
      footer={
        <Space>
          <Button onClick={onClose}>Close</Button>
          {acknowledge && (
            <Button
              type="primary"
              loading={acknowledge.pending}
              onClick={acknowledge.onAcknowledge}
            >
              I have read it
            </Button>
          )}
        </Space>
      }
    >
      {failure ? (
        <div style={{ padding: space[4] }}>
          <Alert
            type="error"
            showIcon
            title="That document could not be loaded"
            description={
              <Space direction="vertical" size="small">
                <Text>{failure}</Text>
                {/* The old route, kept for the case the new one fails. A
                    policy somebody cannot open is one they cannot act on. */}
                {policy && (
                  <a
                    href={policyDocumentUrl(policy.policyId, policy.versionId)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Open it in a new tab instead
                  </a>
                )}
              </Space>
            }
          />
        </div>
      ) : file ? (
        <PolicyDocument
          file={file}
          title={policy?.title ?? 'Policy document'}
          onFailure={onFailure}
        />
      ) : (
        <div
          style={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Spin tip="Loading the document" size="large">
            <span />
          </Spin>
        </div>
      )}
    </Modal>
  );
}
